import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { Gift, Users, ExternalLink, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import ShareCard from "@/components/ShareCard";
import { toast } from "sonner";

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState({ total: 0, referrals: [] });
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/referrals/me");
        setData(res.data);
      } catch (err) {
        logger.error("referrals: failed to load", err);
      }
      setLoading(false);
    })();
  }, []);

  const inviteUrl = user ? `${appUrl}/u/${user.user_id}` : "";

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copiado!");
    } catch {
      toast.error("Falha ao copiar");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24" data-testid="referrals-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">Programa de indicações</p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
          Meus convites
        </h1>
      </div>

      {/* Invite CTA */}
      <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 rounded-2xl p-5 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <Gift size={28} className="text-primary shrink-0" />
          <div>
            <h2 className="font-heading font-bold text-lg">Convide amigos e vire referência</h2>
            <p className="text-sm text-muted-foreground">
              Cada pessoa que se cadastrar pelo seu link ganha um <strong className="text-foreground">destaque grátis</strong> no 1º pedido — e você vira parte da história do amigo no Rivendas.
            </p>
          </div>
        </div>

        <div className="bg-background border border-border rounded-xl p-3 flex items-center gap-2 mb-3">
          <code className="flex-1 text-xs text-muted-foreground truncate font-mono" data-testid="invite-url">
            {inviteUrl}
          </code>
          <Button size="sm" variant="outline" onClick={copyInvite} data-testid="copy-invite">
            Copiar
          </Button>
        </div>

        <Button
          onClick={() => setShareOpen(true)}
          data-testid="referrals-share-btn"
          className="w-full h-11 gap-2 font-bold"
        >
          <Share2 size={16} /> Compartilhar meu link
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <Users size={22} className="text-primary" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Convidados</p>
            <p className="font-heading font-black text-2xl" data-testid="referrals-total">{data.total}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
          <Gift size={22} className="text-primary" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Vendas geradas</p>
            <p className="font-heading font-black text-2xl">
              {data.referrals.reduce((s, r) => s + (r.total_purchases || 0), 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Referrals list */}
      <h3 className="font-heading font-bold text-lg mb-3">Lista de convidados</h3>
      {loading ? (
        <p className="text-muted-foreground text-center py-8">Carregando...</p>
      ) : data.referrals.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground text-sm">
            Ainda ninguém entrou pelo seu link. Que tal compartilhar agora? 🚀
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.referrals.map((r) => (
            <Link
              key={r.user_id}
              to={`/u/${r.user_id}`}
              data-testid={`referral-${r.user_id}`}
              className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:border-primary/50 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/20 text-primary font-heading font-black flex items-center justify-center">
                {r.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground capitalize">
                  {r.role} · {r.city || "sem cidade"}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-muted-foreground">Vendas</p>
                <p className="font-bold">{r.role === "seller" ? r.total_sales : r.total_purchases}</p>
              </div>
              <ExternalLink size={14} className="text-muted-foreground" />
            </Link>
          ))}
        </div>
      )}

      <ShareCard open={shareOpen} onClose={() => setShareOpen(false)} user={user} appUrl={appUrl} />
    </div>
  );
}
